import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMXN, imageUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { MapPin, Bed, Bathtub, Ruler, Heart, User, ArrowLeft, CheckCircle, Image as ImageIcon, WhatsappLogo, ChatCircleText, X } from "@phosphor-icons/react";
import { useFavorites } from "../lib/favorites";
import { useAuth } from "../lib/auth";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ShareMenu } from "../components/ShareMenu";
import { PropertyCard } from "../components/PropertyCard";
import { FlyerModal } from "../components/FlyerModal";

// Persistent per-visitor ID (used to dedupe interest events).
function getVisitorId() {
  try {
    let vid = localStorage.getItem("rf_visitor_id");
    if (!vid) {
      vid = (crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem("rf_visitor_id", vid);
    }
    return vid;
  } catch { return `v-${Date.now()}`; }
}

// Fix default marker icons for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const propertyTypeLabel = {
  casa: "Casa", departamento: "Departamento", terreno: "Terreno", oficina: "Oficina", local: "Local",
};

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [prop, setProp] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(0);
  const { isFav, toggle } = useFavorites();
  const { user } = useAuth();
  const [flyerOpen, setFlyerOpen] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  // Fase 6.1 — Contact CTAs
  const [waOpen, setWaOpen] = useState(false);
  const [waMessage, setWaMessage] = useState("");
  const [waSubmitting, setWaSubmitting] = useState(false);
  const [showConvForm, setShowConvForm] = useState(false);
  const formRef = useRef(null);

  const defaultWaMessage = useMemo(() =>
    prop?.title ? `Hola, me interesa la propiedad ${prop.title}. ¿Podrías darme más información?` : "",
    [prop?.title]
  );

  useEffect(() => {
    setLoading(true);
    api.get(`/properties/${id}`)
      .then(({ data }) => {
        setProp(data);
        setForm(f => ({ ...f, message: `Hola, me interesa la propiedad "${data.title}". Me gustaría más información.` }));
      })
      .catch(() => setProp(false))
      .finally(() => setLoading(false));
    api.get(`/properties/${id}/related`).then(({ data }) => setRelated(data)).catch(() => setRelated([]));
  }, [id]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/properties/${id}/interest`, {
        kind: "conversation",
        visitor_id: getVisitorId(),
        name: form.name, email: form.email, phone: form.phone, message: form.message,
      });
      setSent(true);
      if (data?.duplicate) toast.success("Ya habías enviado una consulta reciente. El asesor la verá pronto.");
      else toast.success("Consulta enviada. El asesor te contactará pronto.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const openWhatsAppFlow = () => {
    setWaMessage(defaultWaMessage);
    setWaOpen(true);
  };
  const confirmWhatsApp = async () => {
    if (waSubmitting) return;
    setWaSubmitting(true);
    try {
      // Register interest first (creates the lead + notifies the agent).
      const { data } = await api.post(`/properties/${id}/interest`, {
        kind: "whatsapp",
        visitor_id: getVisitorId(),
      });
      // Rebuild the wa.me link with the edited message.
      const link = data?.wa_link
        ? data.wa_link.split("?text=")[0] + "?text=" + encodeURIComponent(waMessage || defaultWaMessage)
        : null;
      if (!link) throw new Error("El asesor aún no tiene WhatsApp disponible.");
      window.open(link, "_blank", "noopener,noreferrer");
      if (!data?.duplicate) toast.success("Se avisó al asesor. Continúa en WhatsApp.");
      setWaOpen(false);
    } catch (err) {
      toast.error(formatApiError(err, "No pudimos preparar WhatsApp en este momento."));
    } finally { setWaSubmitting(false); }
  };

  const scrollToForm = () => {
    setShowConvForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center text-[#C8A24A]/70">Cargando...</div>;
  }
  if (!prop) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="font-heading text-3xl">Propiedad no encontrada</h2>
        <Link to="/propiedades"><Button variant="link" className="mt-4">Ver todas</Button></Link>
      </div>
    );
  }

  const fav = isFav(prop.id);
  const hasCoords = prop.latitude && prop.longitude;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <Link to="/propiedades" className="inline-flex items-center gap-2 text-sm text-[#C8A24A]/80 hover:text-[#C8A24A] mb-6" data-testid="back-to-list">
        <ArrowLeft size={16} /> Volver al listado
      </Link>

      {/* Gallery */}
      <div className="grid md:grid-cols-12 gap-3 mb-10">
        <div className="md:col-span-8 aspect-[16/10] rounded-2xl overflow-hidden bg-[#1A1A1A] relative">
          {prop.images?.length > 0 ? (
            <img src={imageUrl(prop.images[selectedImg])} alt={prop.title} className="w-full h-full object-cover" data-testid="detail-main-image" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#1A1A1A] via-[#141414] to-[#0A0A0A]">
              <div className="font-heading text-[#C8A24A]/25 text-7xl tracking-widest">RF</div>
              <div className="mt-3 text-xs uppercase tracking-[0.4em] text-[#C8A24A]/25">
                ROCAFUERTE
              </div>
            </div>
          )}
        </div>
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-3">
          {prop.images?.slice(0, 3).map((img, i) => (
            <button
              key={i}
              onClick={() => setSelectedImg(i)}
              className={`aspect-[16/10] md:aspect-[4/3] rounded-xl overflow-hidden bg-[#1A1A1A] ring-2 transition-[box-shadow,transform] duration-200 ${selectedImg === i ? "ring-[#C8A24A]" : "ring-transparent hover:ring-[#C8A24A]/40"}`}
              data-testid={`thumb-${i}`}
            >
              <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Details */}
        <div className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <div className="flex gap-2 mb-3">
                <span className="px-3 py-1 bg-[#C8A24A]/10 text-[#C8A24A] rounded-full text-xs font-semibold uppercase tracking-wider">
                  {prop.operation === "renta" ? "Renta" : "Venta"}
                </span>
                <span className="px-3 py-1 bg-[#C8A24A]/15 text-[#C8A24A] rounded-full text-xs font-semibold uppercase tracking-wider">
                  {propertyTypeLabel[prop.property_type]}
                </span>
              </div>
              <h1 className="font-heading text-3xl lg:text-5xl tracking-tight leading-[1.1]" data-testid="detail-title">{prop.title}</h1>
              <p className="mt-3 text-[#C8A24A]/80 flex items-start gap-2 text-sm sm:text-base">
                <MapPin size={18} weight="fill" className="text-[#C8A24A] flex-shrink-0 mt-0.5" />
                <span className="min-w-0 break-words">{prop.address}, {prop.city}, {prop.state}</span>
              </p>
            </div>
            <div className="flex gap-2 items-center flex-wrap sm:justify-end">
              <button
                onClick={() => toggle(prop.id)}
                className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-[#C8A24A]/20 flex items-center justify-center hover:border-[#C8A24A] flex-shrink-0"
                data-testid="detail-favorite-btn"
                aria-label="Favorito"
              >
                <Heart size={22} weight={fav ? "fill" : "regular"} className={fav ? "text-[#C8A24A]" : "text-[#C8A24A]"} />
              </button>
              <ShareMenu property={prop} variant="icon" className="w-11 h-11 rounded-full border-[#C8A24A]/20 bg-[#1A1A1A]" />
              {user && user.id && prop.agent_id === user.id && prop.status === "activa" && (prop.images?.length || 0) > 0 && (
                <Button
                  onClick={() => setFlyerOpen(true)}
                  className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 flex-1 sm:flex-none min-w-0"
                  data-testid="detail-flyer-btn"
                >
                  <ImageIcon size={16} weight="duotone" className="mr-1.5" /> Crear flyer
                </Button>
              )}
            </div>
          </div>

          <div className="text-3xl sm:text-4xl font-heading font-bold text-[#C8A24A] mb-8" data-testid="detail-price">
            {formatMXN(prop.price)}
            {prop.operation === "renta" && <span className="text-base sm:text-lg text-[#C8A24A]/60 font-normal ml-2">/ mes</span>}
          </div>

          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
              { icon: Bed, label: "Recámaras", value: prop.bedrooms },
              { icon: Bathtub, label: "Baños", value: prop.bathrooms },
              { icon: Ruler, label: "Superficie", value: `${prop.area_m2} m²` },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="p-5 bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20">
                <Icon size={22} className="text-[#C8A24A] mb-2" weight="duotone" />
                <p className="text-xs uppercase tracking-wider text-[#C8A24A]/70">{label}</p>
                <p className="text-lg font-semibold mt-1">{value}</p>
              </div>
            ))}
          </div>

          <div className="mb-10">
            <h2 className="font-heading text-2xl mb-4">Descripción</h2>
            <p className="text-[#C8A24A]/90 leading-relaxed whitespace-pre-wrap" data-testid="detail-description">
              {prop.description}
            </p>
          </div>

          {prop.features?.length > 0 && (
            <div className="mb-10">
              <h2 className="font-heading text-2xl mb-4">Características</h2>
              <div className="grid grid-cols-2 gap-3">
                {prop.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle size={18} weight="fill" className="text-[#C8A24A]" /> {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasCoords && (
            <div className="mb-10">
              <h2 className="font-heading text-2xl mb-4">Ubicación</h2>
              <div className="h-96 rounded-2xl overflow-hidden border border-[#C8A24A]/20" data-testid="detail-map">
                <MapContainer center={[prop.latitude, prop.longitude]} zoom={14} scrollWheelZoom={false}>
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[prop.latitude, prop.longitude]}>
                    <Popup>{prop.title}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          )}

          {related.length > 0 && (
            <div className="mb-10" data-testid="related-properties">
              <h2 className="font-heading text-2xl mb-4">Propiedades similares</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map(r => <PropertyCard key={r.id} property={r} />)}
              </div>
            </div>
          )}
        </div>

        {/* Contact Sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6">
            <div className="flex items-center gap-3 pb-4 border-b border-[#C8A24A]/20">
              <div className="w-12 h-12 rounded-full bg-[#C8A24A]/10 flex items-center justify-center">
                <User size={22} weight="duotone" className="text-[#C8A24A]" />
              </div>
              <div>
                <p className="font-heading text-lg" data-testid="agent-name">{prop.agent_name}</p>
                <p className="text-xs text-[#C8A24A]/70">Asesor responsable</p>
              </div>
            </div>

            {/* Two primary CTAs — Fase 6.1 */}
            <div className="pt-5 space-y-2.5" data-testid="detail-contact-ctas">
              <Button
                onClick={openWhatsAppFlow}
                className="w-full h-12 bg-[#25D366] hover:bg-[#1EA855] text-black font-semibold"
                data-testid="cta-whatsapp"
              >
                <WhatsappLogo size={18} weight="fill" className="mr-2" /> Contactar por WhatsApp
              </Button>
              <Button
                onClick={scrollToForm}
                variant="outline"
                className="w-full h-12 border-[#C8A24A]/40 hover:bg-[#C8A24A]/10 text-[#C8A24A]"
                data-testid="cta-conversation"
              >
                <ChatCircleText size={18} weight="duotone" className="mr-2" /> Iniciar conversación con el asesor
              </Button>
              <p className="text-xs text-[#C8A24A]/45 leading-relaxed pt-1">
                Ningún mensaje se envía automáticamente. Tú confirmas dentro de WhatsApp o al enviar el formulario.
              </p>
            </div>

            {sent ? (
              <div className="mt-5 p-6 bg-[#C8A24A]/5 rounded-xl text-center" data-testid="inquiry-sent">
                <CheckCircle size={32} weight="fill" className="text-[#C8A24A] mx-auto mb-2" />
                <p className="font-heading text-lg">¡Consulta enviada!</p>
                <p className="text-sm text-[#C8A24A]/70 mt-1">El asesor te contactará pronto.</p>
              </div>
            ) : showConvForm ? (
              <form
                ref={formRef}
                onSubmit={submitInquiry}
                className="mt-5 pt-4 border-t border-[#C8A24A]/20 space-y-3"
                data-testid="inquiry-form"
              >
                <div className="flex items-center justify-between">
                  <p className="font-heading text-lg">Envía tu consulta</p>
                  <button type="button" onClick={() => setShowConvForm(false)} className="text-[#C8A24A]/60 hover:text-[#C8A24A]" aria-label="Cerrar" data-testid="inquiry-form-close">
                    <X size={16} />
                  </button>
                </div>
                <div>
                  <Label htmlFor="name" className="text-xs">Nombre</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="inquiry-name" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs">Correo</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="inquiry-email" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs">Teléfono (opcional)</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-xs">Mensaje</Label>
                  <Textarea id="message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="inquiry-message" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]" data-testid="inquiry-submit-btn">
                  {submitting ? "Enviando..." : "Enviar consulta"}
                </Button>
              </form>
            ) : null}
          </div>
        </aside>
      </div>

      {/* WhatsApp confirmation modal — Fase 6.1 */}
      {waOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" data-testid="wa-confirm-modal">
          <div className="w-full sm:max-w-md bg-[#0F0F0F] border border-[#C8A24A]/30 rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold inline-flex items-center gap-1.5">
                  <WhatsappLogo size={11} weight="fill" /> WhatsApp
                </p>
                <h3 className="font-heading text-xl mt-1">Contactar al asesor</h3>
              </div>
              <button onClick={() => setWaOpen(false)} className="text-[#C8A24A]/60 hover:text-[#C8A24A]" aria-label="Cerrar" data-testid="wa-confirm-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-[#C8A24A]/70 mb-3 leading-relaxed">
              Edita el mensaje inicial si quieres. Se abrirá WhatsApp; tú envías el mensaje directamente en la app.
            </p>
            <Textarea
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              rows={5}
              className="bg-[#0A0A0A] border-[#C8A24A]/25 text-sm resize-y"
              data-testid="wa-message"
              maxLength={600}
            />
            <p className="text-xs text-[#C8A24A]/50 mt-2">
              Al continuar, avisaremos al asesor que alguien mostró interés por WhatsApp. Ningún mensaje se envía automáticamente.
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setWaOpen(false)} className="h-9" data-testid="wa-confirm-cancel">Cancelar</Button>
              <Button
                onClick={confirmWhatsApp}
                disabled={waSubmitting || !waMessage.trim()}
                className="bg-[#25D366] hover:bg-[#1EA855] text-black h-9"
                data-testid="wa-confirm-ok"
              >
                <WhatsappLogo size={14} weight="fill" className="mr-1.5" />
                {waSubmitting ? "Preparando..." : "Continuar a WhatsApp"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Flyer Modal — visible only for the owner agent (Fase 6) */}
      {flyerOpen && (
        <FlyerModal
          open={flyerOpen}
          property={prop}
          onClose={() => setFlyerOpen(false)}
        />
      )}
    </div>
  );
}
